import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {Modal, ModalService} from "../../../../../../services/modal.service";
import {ToastService} from "../../../../../../../common/services/toast.service";
import {SpinnerService} from "../../../../../../../common/services/spinner.service";
import {AttachmentService} from "../../../../../../../common/services/attachment.service";
import {AttachmentsListComponent} from "../../../../../common/components/attachments";
import {FileUploadComponent} from "../../../../../../../common/components/file-upload/file-upload.component";
import {Table} from "../../../../../../../common/interfaces/table.interface";
import {Api} from "../../../../../../../common/services/api.service";
import {Api3Service} from "../../../../../../../common/services/api3.service";
import ICol = Table.ICol;
import {PDFDocument} from "pdf-lib";

@Component({
    selector: "section-order-attachments-list",
    template: `
        <common-confirm></common-confirm>
        <h1 class="container-heading orderAttachmentsHeader">Order attachments</h1>
        <mat-card>
            <mat-card-content>
                <common-table2 *ngIf="listTable" [settings]="listTable" (onRowsSelected)="selectedRows = $event">
                    <div row1>
                        <button mat-raised-button color="accent" [matMenuTriggerFor]="actionsMenu"
                                class="addAttachmentButton">
                            Add attachment
                            <mat-icon>keyboard_arrow_down</mat-icon>
                        </button>

                        <button mat-raised-button color="primary" *ngIf="selectedRows.length" (click)="print()">
                            Print selected
                            <mat-icon>print</mat-icon>
                        </button>
                    </div>
                </common-table2>
            </mat-card-content>
        </mat-card>
        <mat-menu #actionsMenu="matMenu">
            <a mat-menu-item (click)="addAttachment('Warehouse attachment')">Warehouse attachment</a>
            <a mat-menu-item (click)="addAttachment('Invoice')">Invoice</a>
            <a mat-menu-item (click)="addAttachment('Packing list')">Packing list</a>
            <a mat-menu-item (click)="addAttachment()">Other</a>
        </mat-menu>
    `,
    styleUrls: ["list.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderAttachmentsListComponent extends AttachmentsListComponent implements OnInit {

    public selectedRows: any[] = [];

    public constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected attachmentService: AttachmentService,
        protected modalService: ModalService,
        protected toastService: ToastService,
        protected spinnerService: SpinnerService,
        protected api3Service: Api3Service
    ) {
        super(changeDetectorRef, attachmentService, modalService, toastService, spinnerService);
    }

    protected getTableId(): string {
        return "fdj87GFEDdG";
    }

    protected getColumns(): Table.ICol[] {
        const columns: ICol[] = super.getColumns();

        columns.push({
            data: "order.ref",
            name: "Order.ref",
            title: "Order Ref",
            sortable: false,
            searchable: true
        });

        return columns;
    }

    protected getUrl(): Table.ITableApi {
        return {
            url: [this.modal.params.state.section, "orders", this.modal.params.order_id, "attachments"],
            query: {
                relations: [
                    "Order:id,ref"
                ]
            },
            version: 3
        };
    }

    protected prepareList(): void {
        this.listTable = {
            table_id: this.getTableId(),
            actions: this.getActions(),
            api: this.getUrl(),
            columns: this.getColumns(),
            multi_select: function (row: any): boolean {
                return row.extension === "pdf";
            }
        };
        this.changeDetectorRef.markForCheck();
    }

    public async delete(id: number): Promise<any> {
        if (await this.confirmRef.confirm("Are you sure want to delete this attachment?")) {
            this.spinnerService.show();
            const {message, code}: Api.IResponse = await this.api3Service.delete(
                `${this.modal.params.state.section}/orders/${this.modal.params.order_id}/attachments`, {id});

            if (code === 200) {
                this.toastService.show(message, "success");
                this.tableRef.reload();
            }
            this.spinnerService.hide();
        }
    }

    public async addAttachment(category: string = null): Promise<any> {
        const response: Modal.IResponse = await this.modalService.open(FileUploadComponent, {
            url: ["remark", "order", this.modal.params.orderRef, "remark_attachment"],
            body: {
                category
            }
        });

        if (response && response.value) {
            this.toastService.show(response.value.message, "success");
            this.tableRef.reload();
        }
    }

    public async print(): Promise<any> {
        try {
            this.spinnerService.show();
            // Create a fresh PDF document container
            const mergedPdf = await PDFDocument.create();

            // Fetch all PDFs as ArrayBuffers
            const fetchPromises = this.selectedRows.map((row: any) => {
                const cacheBusterUrl = `${row.path}?cb=${new Date().getTime()}`;

                return fetch(cacheBusterUrl).then(res => {
                    if (!res.ok) {
                        throw new Error(`Failed to fetch ${row.path}`);
                    }
                    return res.arrayBuffer();
                });
            });

            const pdfBuffers = await Promise.all(fetchPromises);

            // Loop through and copy pages into the merged document
            for (const buffer of pdfBuffers) {
                const pdfDoc = await PDFDocument.load(buffer);
                const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
                copiedPages.forEach((page) => mergedPdf.addPage(page));
            }

            // Serialize the PDF document to bytes (a Uint8Array)
            const mergedPdfBytes = await mergedPdf.save();

            // Create a local Blob URL
            const blob = new Blob([mergedPdfBytes], {type: "application/pdf"});
            const localBlobUrl = URL.createObjectURL(blob);

            this.spinnerService.hide();

            // Open in a new tab and trigger print
            const newTab = window.open(localBlobUrl, "_blank");
            if (newTab) {
                newTab.onload = () => {
                    newTab.print();
                    // Clean up the URL object from memory after printing starts
                    URL.revokeObjectURL(localBlobUrl);
                };
            } else {
                this.toastService.show("Please allow popups to print files.", "warning");
            }

        } catch (error) {
            this.toastService.show("Error while merging PDFs: " + error.message, "error");
        }
    }

    public ngOnInit(): void {
        this.prepareList();
    }

}
